import {
  isSection,
  surveyDefinitionSchema,
  surveyQuestions,
  type AnonymityMode,
  type SurveyDefinition,
  type SurveyItem,
} from '@voice2me/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SurveyForm, type AnswersState } from '../survey/SurveyForm.js';
import { AnonymityBanner } from '../survey/AnonymityBanner.js';
import { CompletionMessage } from '../survey/CompletionMessage.js';
import { adminApi, newId } from './api.js';
import { ImportQuestions } from './ImportQuestions.js';
import { QuestionEditor, questionOfType } from './QuestionEditor.js';
import { SectionEditor, emptySection } from './SectionEditor.js';

const emptyDefinition = (): SurveyDefinition => ({
  version: 1,
  title: '',
  displayMode: 'list',
  questions: [questionOfType('multiple_choice')],
});

/**
 * Turns a Zod issue into something an HR consultant can act on. Raw issue text names schema
 * paths and talks about string lengths, which is not useful to the person building a survey.
 */
function describeIssue(issue: { path: PropertyKey[]; message: string }): string {
  const path = issue.path;
  if (path[0] === 'title') return 'Give the survey a title';
  if (path[0] !== 'questions') return issue.message;
  if (path.length === 1) return 'Add at least one question';

  const topIndex = typeof path[1] === 'number' ? path[1] + 1 : undefined;

  // A section's own title lives at ['questions', i, 'title'].
  if (path[2] === 'title') {
    return topIndex ? `Section ${topIndex}: give it a title` : 'A section: give it a title';
  }

  // Questions nested in a section sit at ['questions', i, 'questions', j, ...]; standalone
  // questions at ['questions', i, ...]. Resolve the label and field for either shape.
  let questionLabel: string;
  let field: PropertyKey | undefined;
  let optionIndex: PropertyKey | undefined;
  if (path[2] === 'questions') {
    const nestedIndex = typeof path[3] === 'number' ? path[3] + 1 : undefined;
    questionLabel = `Section ${topIndex ?? '?'}, question ${nestedIndex ?? '?'}`;
    field = path[4];
    optionIndex = path[6];
  } else {
    questionLabel = topIndex ? `Question ${topIndex}` : 'A question';
    field = path[2];
    optionIndex = path[4];
  }

  switch (field) {
    case 'prompt':
      return `${questionLabel}: write the question text`;
    case 'options':
      return typeof optionIndex === 'number'
        ? `${questionLabel}, option ${optionIndex + 1}: give it a label`
        : `${questionLabel}: add at least two options`;
    case 'max':
      return `${questionLabel}: the top of the scale must be higher than the bottom`;
    default:
      return `${questionLabel}: ${issue.message}`;
  }
}

const anonymityCopy: Record<AnonymityMode, string> = {
  anonymous: 'Answers are never linked to the person who gave them.',
  opt_in: 'Answers are anonymous unless the employee ticks a box to be identified.',
  identified: 'Answers are always linked to the employee who gave them.',
};

export function SurveyEditorPage() {
  const { surveyId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = !surveyId;

  const vendors = useQuery({ queryKey: ['vendors'], queryFn: adminApi.vendors });
  const existing = useQuery({
    queryKey: ['survey-detail', surveyId],
    enabled: !isNew,
    queryFn: () => adminApi.survey(surveyId!),
  });

  const [vendorId, setVendorId] = useState(searchParams.get('vendorId') ?? '');
  const [slug, setSlug] = useState('');
  const [anonymityMode, setAnonymityMode] = useState<AnonymityMode>('anonymous');
  const [definition, setDefinition] = useState<SurveyDefinition>(emptyDefinition);
  const [previewAnswers, setPreviewAnswers] = useState<AnswersState>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!existing.data) return;
    setVendorId(existing.data.vendorId);
    setSlug(existing.data.slug);
    setAnonymityMode(existing.data.anonymityMode);
    setDefinition(existing.data.definition);
  }, [existing.data]);

  const isDraft = isNew || existing.data?.status === 'draft';
  const selectedVendorLogo = vendors.data?.find((vendor) => vendor.id === vendorId)?.logoUrl ?? null;

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) {
        return adminApi.createSurvey({ vendorId, slug, anonymityMode, definition });
      }
      return adminApi.updateSurvey(surveyId!, { vendorId, slug, anonymityMode, definition });
    },
    onSuccess: async (survey) => {
      setSaveError(null);
      await queryClient.invalidateQueries({ queryKey: ['surveys'] });
      await queryClient.invalidateQueries({ queryKey: ['survey-detail', survey.id] });
      if (isNew) navigate(`/admin/surveys/${survey.id}`, { replace: true });
    },
    onError: (error) => setSaveError(error.message),
  });

  const publish = useMutation({
    mutationFn: () => adminApi.publish(surveyId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      navigate('/admin');
    },
    onError: (error) => setSaveError(error.message),
  });

  const validation = surveyDefinitionSchema.safeParse(definition);
  const definitionProblems = validation.success
    ? []
    : [...new Set(validation.error.issues.map(describeIssue))];
  if (!vendorId) definitionProblems.unshift('Choose which company this survey is for');
  if (!/^[a-z0-9-]+$/.test(slug)) {
    definitionProblems.unshift('Set a URL slug (lowercase letters, numbers and hyphens)');
  }
  const canSave = Boolean(vendorId) && /^[a-z0-9-]+$/.test(slug) && validation.success;

  // Editor operations act on the top-level content list, whose entries are questions or sections.
  function updateItem(index: number, item: SurveyItem) {
    setDefinition((current) => ({
      ...current,
      questions: current.questions.map((candidate, position) =>
        position === index ? item : candidate,
      ),
    }));
  }

  function removeItem(index: number) {
    setDefinition((current) => ({
      ...current,
      questions: current.questions.filter((_unused, position) => position !== index),
    }));
  }

  function moveItem(index: number, direction: -1 | 1) {
    setDefinition((current) => {
      const questions = [...current.questions];
      const target = index + direction;
      const moving = questions[index];
      const displaced = questions[target];
      if (!moving || !displaced) return current;
      questions[index] = displaced;
      questions[target] = moving;
      return { ...current, questions };
    });
  }

  function appendItem(item: SurveyItem) {
    setDefinition((current) => ({ ...current, questions: [...current.questions, item] }));
  }

  if (!isNew && existing.isPending) return <p>Loading…</p>;

  return (
    <>
      <h2>{isNew ? 'New survey' : `Editing: ${definition.title || 'untitled'}`}</h2>

      {!isDraft && (
        <div className="banner banner-identified">
          <strong>This survey is {existing.data?.status}.</strong>
          <p>
            Its questions are frozen because answers already reference them. Clone it from the
            survey list to make changes.
          </p>
        </div>
      )}

      <div className="editor-layout">
        <section className="editor-pane">
          <h3>Setup</h3>

          <label>
            Company
            <select
              value={vendorId}
              disabled={!isDraft}
              onChange={(event) => setVendorId(event.target.value)}
            >
              <option value="">Choose a company…</option>
              {vendors.data?.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            URL slug
            <input
              type="text"
              value={slug}
              disabled={!isDraft}
              placeholder="q3-wellbeing"
              onChange={(event) => setSlug(event.target.value)}
            />
          </label>
          {vendorId && slug && (
            <p className="note">
              Employees will use <code>/{vendors.data?.find((v) => v.id === vendorId)?.slug}/{slug}</code>
            </p>
          )}

          <label>
            Anonymity
            <select
              value={anonymityMode}
              disabled={!isDraft}
              onChange={(event) => setAnonymityMode(event.target.value as AnonymityMode)}
            >
              <option value="anonymous">Anonymous</option>
              <option value="opt_in">Employee chooses</option>
              <option value="identified">Identified</option>
            </select>
          </label>
          <p className="note">{anonymityCopy[anonymityMode]}</p>

          <label>
            Title
            <input
              type="text"
              value={definition.title}
              disabled={!isDraft}
              onChange={(event) =>
                setDefinition((current) => ({ ...current, title: event.target.value }))
              }
            />
          </label>

          <label>
            Introduction paragraph (optional)
            <textarea
              rows={3}
              value={definition.description ?? ''}
              disabled={!isDraft}
              onChange={(event) =>
                setDefinition((current) => ({ ...current, description: event.target.value }))
              }
            />
          </label>

          <label>
            Agreement (optional)
            <textarea
              rows={3}
              value={definition.agreement ?? ''}
              disabled={!isDraft}
              onChange={(event) =>
                setDefinition((current) => ({
                  ...current,
                  agreement: event.target.value || undefined,
                }))
              }
            />
          </label>
          <p className="note">
            Replaces the default wording in the anonymity notice shown before the survey starts, for
            example a confidentiality note or participation terms. The acknowledgement checkbox stays.
          </p>

          <label>
            Final message (optional)
            <textarea
              rows={3}
              value={definition.completionMessage ?? ''}
              disabled={!isDraft}
              onChange={(event) =>
                setDefinition((current) => ({
                  ...current,
                  completionMessage: event.target.value || undefined,
                }))
              }
            />
          </label>
          <p className="note">
            Shown on the thank-you screen after an employee submits, in place of the default
            “Your response has been recorded.”
          </p>

          <label className="inline">
            <input
              type="checkbox"
              disabled={!isDraft}
              checked={definition.showCompletionTime ?? false}
              onChange={(event) =>
                setDefinition((current) => ({
                  ...current,
                  showCompletionTime: event.target.checked,
                }))
              }
            />
            Show how long the survey took on the final screen
          </label>
          <p className="note">
            Adds a “Completed in: X mins X secs” line to the thank-you screen, timed in the
            employee’s browser from opening the survey to submitting. It is shown only to them and is
            never stored.
          </p>

          <label className="inline">
            <input
              type="checkbox"
              disabled={!isDraft}
              checked={definition.displayMode === 'slideshow'}
              onChange={(event) =>
                setDefinition((current) => ({
                  ...current,
                  displayMode: event.target.checked ? 'slideshow' : 'list',
                }))
              }
            />
            Slideshow mode: show one question per slide
          </label>
          <p className="note">
            {definition.displayMode === 'slideshow'
              ? 'Employees answer one question at a time and advance with a Next button.'
              : 'Employees see every question on a single scrolling page.'}
          </p>

          <h3>Questions</h3>
          {isDraft ? (
            <>
              {definition.questions.map((item, index) =>
                isSection(item) ? (
                  <SectionEditor
                    key={item.id}
                    section={item}
                    index={index}
                    total={definition.questions.length}
                    onChange={(updated) => updateItem(index, updated)}
                    onMove={(direction) => moveItem(index, direction)}
                    onRemove={() => removeItem(index)}
                  />
                ) : (
                  <QuestionEditor
                    key={item.id}
                    question={item}
                    index={index}
                    total={definition.questions.length}
                    onChange={(updated) => updateItem(index, updated)}
                    onMove={(direction) => moveItem(index, direction)}
                    onRemove={() => removeItem(index)}
                  />
                ),
              )}
              <div className="editor-add-actions">
                <button type="button" onClick={() => appendItem(questionOfType('multiple_choice'))}>
                  Add question
                </button>
                <button type="button" onClick={() => appendItem(emptySection())}>
                  Add section
                </button>
              </div>

              <ImportQuestions
                onImport={(imported) =>
                  setDefinition((current) => ({
                    ...current,
                    questions: [...current.questions, ...imported],
                  }))
                }
              />
            </>
          ) : (
            <p className="note">{surveyQuestions(definition).length} questions, frozen.</p>
          )}

          {isDraft && definitionProblems.length > 0 && (
            <div className="checklist">
              <strong>Still to do before this can be saved</strong>
              <ul>
                {definitionProblems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}

          {saveError && <p role="alert">{saveError}</p>}

          {isDraft && (
            <div className="editor-actions">
              <button type="button" onClick={() => save.mutate()} disabled={!canSave || save.isPending}>
                {save.isPending ? 'Saving…' : 'Save draft'}
              </button>
              {!isNew && (
                <button
                  type="button"
                  onClick={() => publish.mutate()}
                  disabled={!canSave || publish.isPending}
                  title="Publishing freezes the questions"
                >
                  Publish
                </button>
              )}
              <button type="button" onClick={() => navigate('/admin')}>
                Back to surveys
              </button>
            </div>
          )}
        </section>

        <section className="preview-pane">
          <h3>Preview</h3>
          <p className="note">Exactly what an employee sees.</p>
          <div className="preview">
            {selectedVendorLogo && (
              <img className="survey-logo" src={selectedVendorLogo} alt="Company logo" />
            )}
            <h1>{definition.title || 'Untitled survey'}</h1>
            {/* In slideshow mode the introduction is the first slide, so don't also show it here. */}
            {definition.displayMode !== 'slideshow' && definition.description && (
              <p className="description">{definition.description}</p>
            )}
            <AnonymityBanner
              mode={anonymityMode}
              consent={false}
              onConsentChange={() => {}}
              agreement={definition.agreement}
            />
            <SurveyForm
              definition={definition}
              answers={previewAnswers}
              onAnswerChange={(questionId, value) =>
                setPreviewAnswers((current) => ({ ...current, [questionId]: value }))
              }
            />
          </div>

          <p className="note preview-subhead">After submitting</p>
          <div className="preview">
            <h1>Thank you</h1>
            <CompletionMessage message={definition.completionMessage} />
            {definition.showCompletionTime && (
              <p className="completion-time">Completed in: 2 mins 30 secs</p>
            )}
          </div>
          {definition.showCompletionTime && (
            <p className="note">The time shown here is an example; it is measured live for each employee.</p>
          )}
        </section>
      </div>
    </>
  );
}

/** Exported for the list page's "new survey" button, so ids stay consistent across the app. */
export { newId };
